import React from 'react'
import { useTranslation } from 'react-i18next'
import { formatNumberEnglish } from '../../utils'
import CurrencyDisplay from '../shared/CurrencyDisplay'

interface OrderItemRowProps {
  item: any
  isRTL: boolean
}

const OrderItemRow: React.FC<OrderItemRowProps> = ({ item, isRTL }) => {
  const { t } = useTranslation()
  // Extract item details - prioritize meals array from API structure
  // API structure: item.meals[0].name
  const productName = item.meals?.[0]?.name || 
                      item.meal?.name ||
                      item.product?.name || 
                      item.product_name || 
                      item.meal?.product?.name || 
                      item.name || 
                      t('order.unknown_item')
  const quantity = parseInt(String(item.quantity || '1'))
  
  // Get price - try different fields
  const unitPrice = parseFloat(String(item.price || item.unit_price || item.meal?.price || '0'))
  const totalPrice = parseFloat(String(item.total_price || item.total_unit_price || (unitPrice * quantity)))
  
  // Extract modifiers/additionals - try different structures
  // Priority: meals[0].additionals (API structure) > item.additionals > other fields
  const meal = item.meals?.[0]
  const mealAdditionals = meal?.additionals || []
  const extras = item.extras_display || item.additionals_display || item.extras || item.additionals || ''
  let modifierNames = ''
  
  // First check meal additionals (most common API structure)
  if (Array.isArray(mealAdditionals) && mealAdditionals.length > 0) {
    modifierNames = mealAdditionals
      .filter((m: any) => m != null)
      .map((m: any) => {
        if (typeof m === 'string') return m
        return m.name || m.product?.name || String(m)
      })
      .filter(Boolean)
      .join(' - ')
  } else if (typeof extras === 'string' && extras.trim()) {
    modifierNames = extras
  } else if (Array.isArray(extras) && extras.length > 0) {
    modifierNames = extras
      .filter((m: any) => m != null)
      .map((m: any) => {
        if (typeof m === 'string') return m
        return m.name || m.product?.name || String(m)
      })
      .filter(Boolean)
      .join(' - ')
  } else if (Array.isArray(item.additionals) && item.additionals.length > 0) {
    modifierNames = item.additionals
      .filter((m: any) => m != null)
      .map((m: any) => {
        if (typeof m === 'string') return m
        return m.name || m.product?.name || String(m)
      })
      .filter(Boolean)
      .join(' - ')
  }

  return (
    <div className="w-full py-2.5 px-1 flex justify-between items-start">
      {/* Item Info - Right side */}
      <div className="flex-1 flex flex-col justify-start items-start gap-1">
        <div className="flex items-start gap-2">
          <div className="text-right text-text-primary text-base font-semibold font-['IBM_Plex_Sans_Arabic']">
            {productName}
          </div>
          <div className="px-1.5 py-0.5 text-gray-700 text-sm font-bold font-['IBM_Plex_Sans_Arabic']">
            x{quantity}
          </div>
        </div>
        {modifierNames && (
          <div className="w-[150px] text-right text-gray-700 text-xs font-normal font-['IBM_Plex_Sans_Arabic']">
            {modifierNames}
          </div>
        )}
      </div>

      {/* Price - Left side */}
      <div className="flex items-center gap-1">
        <span className="text-[#525866] text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
          {formatNumberEnglish(totalPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
        <CurrencyDisplay className="w-4 h-4" />
      </div>
    </div>
  )
}

export default OrderItemRow

